/**
 * One answer to "what happens when you click a subject", for every table and
 * canvas that shows one.
 *
 * The inspector is a WAY OF WORKING — "stay where I am" versus "navigate out" —
 * not a per-surface display option. A page where clicking a graph node opened a
 * panel while clicking a table row navigated away does not read as two features;
 * it reads as broken. So the preference is one preference, and this is the one
 * place that spends it.
 *
 * It exists because that had already drifted. The case workspace honoured the
 * preference on its rows, its nodes, its edges and its collections. The
 * transactions explorer — the largest table in the product — navigated
 * unconditionally, so the switch on the case graph, whose hint reads "Applies to
 * the table too", was telling the truth about one table and not the other. The
 * fix is not to copy four handlers into the explorer: it is for there to be one
 * set of handlers that cannot disagree, which is this.
 *
 * The toggle ships with it, in {@link SubjectOpener.toggle}, for the same
 * reason. A control that governs the table belongs on the table, and until now
 * the only ways to reach it were the graph's gear and the settings page —
 * neither of which is where somebody is standing when they wish a row had opened
 * beside them instead of taking the page.
 */
import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { txRowId, type TxRow } from "@authe/tx-table";
import { ROUTES } from "@/config";
import type { FocusTarget } from "@authe/fe-commons/focus";
import { subjectFromFocus } from "./model";
import { useInspector, type InspectorController } from "./useInspector";
import { useInspectorPref } from "./useInspectorPref";

/**
 * Whether a click on an ADDRESS can be answered in the panel.
 *
 * The only branch here with a real decision in it, and the reason it is pulled
 * out where a test can reach it: an address on its own is not an inspectable
 * subject — the panel resolves attribution per (chain, address) — so a click
 * that arrives without a chain has to navigate even with the preference on. The
 * page then resolves the chain itself, which a panel could not.
 *
 * Getting this backwards opens an empty panel and looks like a broken click,
 * which is worse than the navigation it replaced.
 */
export function opensAddressInPanel(
  enabled: boolean,
  chain: string | null | undefined,
): boolean {
  return Boolean(enabled && chain);
}

/** The toggle's shape — the same one the graph's settings gear takes. */
export interface SubjectOpenerToggle {
  key: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}

/**
 * The controller of the panel an enclosing page renders, for links inside it.
 *
 * A link builds its own opener, and its own controller has no panel. Inside a
 * page that does render one, the link should open THAT panel — so the page
 * publishes its controller here with {@link InspectorHost}.
 */
const InspectorHostContext = React.createContext<InspectorController | null>(null);

export function InspectorHost({
  inspector,
  children,
}: {
  inspector: InspectorController;
  children: React.ReactNode;
}): React.ReactElement {
  return <InspectorHostContext.Provider value={inspector}>{children}</InspectorHostContext.Provider>;
}

export interface SubjectOpener {
  /** The controller, for surfaces that open richer targets (edges, collections). */
  inspector: InspectorController;
  /** The preference. Whether a click can open a panel HERE is {@link inPanel}. */
  enabled: boolean;
  /**
   * The preference is on AND a panel is mounted for this opener's controller.
   * Off means the click navigates — never that it does nothing.
   */
  inPanel: boolean;
  setEnabled: (next: boolean) => void;
  /** Open a transaction row — panel when the preference is on, else its page. */
  openTransaction: (row: TxRow | Record<string, unknown>) => void;
  /**
   * The same, for a caller that already has the canonical id (a graph node, a
   * focus target). `openTransaction` is this with `txRowId` in front of it.
   */
  openTransactionId: (canonicalTxId: string) => void;
  /** Open an address — `chain` is carried so a multi-chain page preselects it. */
  openAddress: (address: string, chain?: string | null) => void;
  toggle: SubjectOpenerToggle;
}

export function useSubjectOpener(options: { ownPanel?: boolean } = {}): SubjectOpener {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useInspectorPref();
  const own = useInspector({ enabled });
  const host = React.useContext(InspectorHostContext);
  // A surface that renders its own panel (`ownPanel`) keeps its own controller,
  // so a panel is never drawn twice for one; everything else opens the
  // enclosing page's panel when there is one.
  const inspector = options.ownPanel ? own : host ?? own;
  const inPanel = inspector.enabled && inspector.hasPanel;

  const openTransactionId = React.useCallback(
    (canonicalTxId: string) => {
      if (!canonicalTxId) return;
      if (inPanel) {
        inspector.open({ kind: "transaction", canonicalTxId });
        return;
      }
      navigate({ to: "/transactions/$id", params: { id: canonicalTxId } });
    },
    [inPanel, inspector, navigate],
  );

  const openTransaction = React.useCallback(
    (row: TxRow | Record<string, unknown>) => {
      openTransactionId(txRowId(row as TxRow) ?? "");
    },
    [openTransactionId],
  );

  const openAddress = React.useCallback(
    (address: string, chain?: string | null) => {
      if (!address) return;
      if (opensAddressInPanel(inPanel, chain)) {
        inspector.open({ kind: "address", chainId: chain as string, address });
        return;
      }
      navigate({
        to: ROUTES.address(address),
        ...(chain ? { search: { chain } } : {}),
      } as never);
    },
    [inPanel, inspector, navigate],
  );

  const toggle = React.useMemo<SubjectOpenerToggle>(
    () => ({
      key: "inspector",
      label: "Open clicks in a side panel",
      // Said on every surface that offers it, because a switch sitting in one
      // place reads as governing that place.
      hint: "Applies to tables and graphs alike",
      checked: enabled,
      onChange: setEnabled,
    }),
    [enabled, setEnabled],
  );

  return {
    inspector,
    enabled,
    inPanel,
    setEnabled,
    openTransaction,
    openTransactionId,
    openAddress,
    toggle,
  };
}

/**
 * What a FOCUS TARGET does when it is clicked — the graph node, the decoded
 * field, the attribution chip.
 *
 * The same answer as a row click, and for the same reason the opener exists at
 * all: a page where clicking a node opened a panel while clicking a field of the
 * same transaction navigated away reads as broken. Two pages had written this
 * out (the transactions explorer and the case workspace) and the copies had
 * already disagreed — the explorer's navigated unconditionally, ignoring the
 * preference its own toolbar toggle sets.
 *
 * A target with no id does nothing; an address with no chain cannot be answered
 * in the panel (`subjectFromFocus` returns null for it) and navigates, which is
 * where the page resolves the chain the panel could not.
 */
export function useFocusDrilldown(providedOpener?: SubjectOpener): (target: FocusTarget) => void {
  const inheritedOpener = useSubjectOpener();
  // A page's own hooks cannot see the InspectorHost it returns below them.
  // Share its mounted controller explicitly; descendants inherit the host.
  const opener = providedOpener ?? inheritedOpener;
  return React.useCallback(
    (target: FocusTarget) => {
      if (!target.id) return;
      const subject = subjectFromFocus(target);
      if (opener.inPanel && subject) {
        opener.inspector.open(subject);
        return;
      }
      if (target.kind === "address") {
        opener.openAddress(target.id, target.chain);
        return;
      }
      opener.openTransactionId(target.id);
    },
    [opener],
  );
}
